import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { getCreditPackages, purchaseCredits,} from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  initiateWebViewPayment,
  generateTransactionId, 
  normalizePhoneNumber, 
  isNativeApp 
} from '../services/payuWebViewService';

function CreditPurchase() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchParams = new URLSearchParams(location.search);

  const purchaseSectionRef = useRef(null);

  // ✅ Fetch credit packages from API on component mount
  useEffect(() => {
    const fetchCreditPackages = async () => {
      try {
        setIsLoading(true);
        setError(null);
        console.log('📦 Fetching credit packages from backend API...');

        const response = await getCreditPackages();

        if (response.data && response.data.success) {
          const packages = response.data.packages.map(pkg => ({
            id: pkg.packageId,
            credits: pkg.baseCredits,
            price: pkg.price,
            badge: pkg.packageName,
            badgeColor: pkg.badgeColor,
            bonus: pkg.bonusCredits,
            totalCredits: pkg.totalCredits,
            description: pkg.description,
            features: pkg.features
          }));

          setCreditPackages(packages);
          console.log('✅ Credit packages loaded:', packages);
        } else {
          throw new Error('Failed to fetch credit packages');
        }
      } catch (error) {
        console.error('❌ Error fetching credit packages:', error);
        setError('Failed to load credit packages. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCreditPackages();
  }, []);

  // ✅ Handle package selection
  const handlePackageSelect = (packageData) => {
    setSelectedPackage(packageData);
    console.log('📦 Package selected:', packageData);

    setTimeout(() => {
      if (purchaseSectionRef.current) {
        purchaseSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // ✅ WebView Payment Handler 
  const handlePurchase = async () => {
    if (!selectedPackage || isProcessing) {
      if (!selectedPackage) {
        alert('⚠️ Please select a credit package first!');
      }
      return;
    }

    if (!user?.userId) {
      alert('❌ User not found. Please log in again.');
      navigate('/login');
      return;
    }

    setIsProcessing(true);
    setError(null);
    console.log('💳 Processing purchase with PayU WebView:', selectedPackage);
    console.log('📱 Platform:', isNativeApp() ? 'Native App' : 'Web Browser');

    try {
      const txnid = generateTransactionId(user.userId, selectedPackage.id);
      const phoneNumber = normalizePhoneNumber(
        user?.phone || user?.mobile || user?.phoneNumber || user?.contact || ''
      );

      console.log('📱 Phone used for PayU payment:', phoneNumber);

      // 1) Create purchase record
      const createPurchaseResp = await purchaseCredits(
        user.userId, 
        selectedPackage.id, 
        txnid, 
        'credit_pack',
        isNativeApp() ? 'mobile' : 'web',
        'PAYU',
        'online',
        'INR',
        'pending'
      );

      const createdPurchaseId = createPurchaseResp?.data?.purchase?.purchaseId || null;
      if (!createdPurchaseId) {
        throw new Error('Failed to create purchase record before payment');
      }
      console.log('🔖 Created purchase record, purchaseId:', createdPurchaseId);

      // 2) Payment data
      const paymentData = {
        txnid,
        amount: selectedPackage.price,
        productinfo: `${selectedPackage.badge} - ${selectedPackage.totalCredits} Credits`,
        firstname: user?.full_name || user?.name || 'Customer',
        email: user?.email || '',
        phone: phoneNumber || '9999999999',
        udf1: String(user?.userId),
        udf2: String(createdPurchaseId),
        packageId: selectedPackage.id,
        purchaseId: createdPurchaseId,
        credits: selectedPackage.totalCredits
      };

       // 3) Open PayU using service function
      console.log('💳 Initiating payment via WebView service...');
      await initiateWebViewPayment(paymentData);
      console.log('✅ PayU opened ');

    } catch (error) {
      console.error('❌ Payment error:', error);
      if (error.response?.status === 409) {
        alert('⚠️ This transaction has already been processed.');
      } else if (error.response?.status === 400 && error.response?.data?.error === 'Credit limit exceeded') {
        const whatsappMessage = encodeURIComponent(
          `${user.full_name || user.name} (${user.email})\n` +
          `Limit extension request\n\n` +
          `Please approve. Thanks!`
        );

        const userResponse = window.confirm(
          `⚠️ Your credit limit has been exceeded!\n\n` +
          `Please contact support for limit extension:\n` +
          `📞 Hemant Rathore\n` +
          `Mobile: +91 8866822366\n\n` +
          `Would you like to contact support via WhatsApp?`
        );
        if (userResponse) {
          window.open(`https://wa.me/918866822366?text=${whatsappMessage}`, '_blank');
        }
      } else {
        const errorMessage = error.response?.data?.error || error.message || 'Payment initialization failed';
        alert(`❌ ${errorMessage}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="credit-purchase-container" >
      {/* Main Content */}
      <div className="credit-content">
        {/* Title Section */}
        <div className="credit-title-section">
          <h1 className="heading">💎 Get More Cosmic Credits</h1>
          <p className="refer-subtitle">
            Unlock unlimited cosmic wisdom with our credit packages.<br />
            Each question costs 10 credits for Quick Answers and 15 credits for Detailed Answers.
          </p>

          {/* Current Balance Display */}
          <div className="current-balance-display">
            <span className="balance-label">Your Current Balance:</span>
            <span className='dimond'>💎</span>
            <span className="balance-amount"> {user?.credits || 0} Credits</span>
            {user?.credits < 10 && <span className="low-credits-warning">⚠️ Low Credits</span>}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>⚠️</span>
            <span>{error}</span>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="loading-message">
            <span>⏳ Loading credit packages...</span>
          </div>
        )}

        {/* Credit Packages Grid */}
        {!isLoading && (
          <div className="credit-packages-grid">
            {creditPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`credit-package ${pkg.badgeColor} ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                onClick={() => handlePackageSelect(pkg)}
              >
                {/* Badge */}
                <div className={`package-badge badge-${pkg.badgeColor}`}>
                  {pkg.badge}
                </div>

                <div className="package-content">
                  {/* Selection Indicator */}
                  {selectedPackage?.id === pkg.id && (
                    <div className="selection-indicator">
                      <span>✓</span>
                    </div>
                  )}

                  {/* Credits Display */}
                  <div className="package-credits">{pkg.credits}</div>
                  <div className="credits-label">
                    <span className='dimond'>💎</span>
                    <span>Credits</span>
                  </div>

                  {/* Bonus Credits */}
                  {pkg.bonus > 0 && (
                    <div className="bonus-credits">
                      🎁 +{pkg.bonus} Bonus Credits
                    </div>
                  )}

                  {/* Price */}
                  <div className="package-price">₹{pkg.price}</div>

                  {/* Description */}
                  <div className="package-description">{pkg.description}</div>

                  {/* Value Calculation */}
                  <div className="package-value">
                    Total: {pkg.totalCredits} credits •
                    ₹{(pkg.price / pkg.totalCredits).toFixed(1)} per credit
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Purchase Section */}
        <div className="purchase-section" ref={purchaseSectionRef}>
          <button
            className={`main-purchase-btn ${selectedPackage ? 'enabled' : 'disabled'}`}
            onClick={handlePurchase}
            disabled={!selectedPackage || isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="btn-icon">⏳</span>
                <span>Processing Payment...</span>
              </>
            ) : selectedPackage ? (
              <>
                <span className="btn-icon">🛒</span>
                <span>Purchase {selectedPackage.badge} - ₹{selectedPackage.price}</span>
              </>
            ) : (
              <>
                <span className="btn-icon">📦</span>
                <span>Select a Package to Continue</span>
              </>
            )}
          </button>

          {/* Security Info */}
          <div className="step-content">
            <p>🔒 Secure payment powered by PayU • 256-bit SSL encryption</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreditPurchase;
